import { SlidersHorizontal } from 'lucide-react';
import { TableActionMenu, type PanelSpan, type TableActionItem } from '@/components/shared';
import type { BackofficeOrganizationDetail, BackofficeOrganizationFeatures } from '../types';
import { OrganizationDetailPanel } from './OrganizationDetailPanelGrid';

export interface OrganizationFeaturesPanelProps {
  organization: BackofficeOrganizationDetail;
  panelId?: string | undefined;
  currentSpan?: PanelSpan | undefined;
  onEditFeatures: () => void;
}

const FEATURE_ROWS: Array<{
  key: keyof BackofficeOrganizationFeatures;
  label: string;
}> = [
  { key: 'insights', label: 'Nava Insights' },
  { key: 'organizations', label: 'Organizations members' },
  { key: 'sso', label: 'Single sign-on' },
  { key: 'domains', label: 'Domains' },
  { key: 'integrations', label: 'Integrations' },
  { key: 'apikeys', label: 'API keys' },
  { key: 'bringYourOwnParticipants', label: 'Bring your own participants' },
  { key: 'customParticipantPrivacy', label: 'Custom participant privacy' },
  { key: 'interviewRecordingPrivacy', label: 'Interview recording privacy' },
  { key: 'collectParticipantEmail', label: 'Collect participant email' },
  { key: 'workspaces', label: 'Workspaces' },
  { key: 'customerListening', label: 'Customer listening' },
  { key: 'customerListeningSms', label: 'Customer listening SMS' },
  { key: 'outboundPhoneCalls', label: 'Outbound phone calls' },
];

export function OrganizationFeaturesPanel({
  organization,
  panelId,
  currentSpan,
  onEditFeatures,
}: OrganizationFeaturesPanelProps) {
  const actions: TableActionItem[] = [
    {
      id: 'edit-features',
      label: "Edit org's features",
      icon: SlidersHorizontal,
      placement: 'menu',
      onSelect: onEditFeatures,
    },
  ];

  return (
    <OrganizationDetailPanel
      title="Features"
      span="2x2"
      panelId={panelId}
      currentSpan={currentSpan}
      headerActions={<TableActionMenu actions={actions} label="Features tile actions" />}
    >
      <dl className="divide-y divide-neutral-100 text-body">
        {FEATURE_ROWS.map((feature) => {
          const enabled = organization.features[feature.key];
          return (
            <div
              key={feature.key}
              className="grid gap-2 py-3 first:pt-0 last:pb-0 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center"
            >
              <dt
                className={
                  feature.key === 'sso'
                    ? 'ml-5 border-l border-neutral-200 pl-4 font-semibold text-neutral-900'
                    : 'font-semibold text-neutral-900'
                }
              >
                {feature.label}
              </dt>
              <dd>
                <span
                  className={[
                    'inline-flex rounded-full px-2.5 py-1 text-caption font-semibold',
                    enabled ? 'bg-emerald-50 text-emerald-700' : 'bg-neutral-100 text-neutral-600',
                  ].join(' ')}
                >
                  {enabled ? 'Enabled' : 'Disabled'}
                </span>
              </dd>
            </div>
          );
        })}
      </dl>
    </OrganizationDetailPanel>
  );
}
